/**
 * Resource-reference questions the semantic linter rules ask. Every answer comes
 * from `parseResourceReference`, `findSubResource` and `findExtResource`, not a
 * local regex or scan, so a reference cannot resolve for the renderer and read as
 * missing to a rule.
 */
import { findExtResource, findSubResource, parseResourceReference, } from '../resources/SubResourceResolver.js';
import { heldResource, holdsResource } from '../godot/scene/resourceLiteral.js';
/**
 * Whether a resource slot holds nothing: {@link holdsResource} asked the other way round. The call-site
 * sweep in `clearedResourceSlot.test.ts` takes its predicate names from this module's exports, and
 * `resourceLiteral.ts`'s.
 */
export function resourceSlotIsEmpty(resourceRef) {
    return !holdsResource(resourceRef);
}
/**
 * Resolve a raw property value against the scene's resource tables, once. The
 * rules branch on this answer, and {@link checkResourceExists} derives from it.
 *
 * @param scene - the parsed scene the reference is resolved against.
 * @param resourceRef - a raw property value, such as `SubResource("Box_1")`.
 */
export function resolveResourceSlot(scene, resourceRef) {
    const held = heldResource(resourceRef);
    if (held === undefined)
        return { kind: 'empty' };
    const parsed = parseResourceReference(held);
    if (parsed === null)
        return { kind: 'not-a-reference' };
    const declared = parsed.type === 'SubResource'
        ? findSubResource(scene.internalResources ?? [], parsed.id)
        : findExtResource(scene.externalResources ?? [], parsed.id);
    return declared ? { kind: 'resolved', type: declared.type } : { kind: 'dangling' };
}
/**
 * Whether a reference is anything other than a dangling one. A non-reference and
 * a cleared slot are both `true`: only a well-formed reference can dangle. The
 * dangling error is `danglingResources.ts`'s. This serves a rule that gates
 * something else on a resolving reference, such as Path2D's script exemption.
 *
 * @example
 * ```typescript
 * if (!checkResourceExists(scene, 'SubResource("mesh_1")')) {
 *   // Report error: resource not found
 * }
 * ```
 */
export function checkResourceExists(scene, resourceRef) {
    return resolveResourceSlot(scene, resourceRef).kind !== 'dangling';
}
//# sourceMappingURL=resourceChecker.js.map